<template>
  <nav-header :title="$englishPreferred ? 'Ranking' : 'Rangliste'"></nav-header>
  <main
    class="statistics-page ranking-page"
    :class="{ 'is-finn-ranking': isFinn }"
  >
    <div class="container statistics-container">
      <header id="titleDiv" class="ranking-title-card">
        <h1>{{ $englishPreferred ? headingEnglish : headingDanish }}</h1>
      </header>

      <template v-if="rankRows.length > 0">
        <section class="ranking-toolbar" aria-label="Ranking controls">
          <label class="ranking-search">
            <i class="material-icons" aria-hidden="true">search</i>
            <input
              v-model.trim="searchQuery"
              type="search"
              :placeholder="
                $englishPreferred ? 'Search for a name' : 'Søg efter et navn'
              "
              :aria-label="
                $englishPreferred
                  ? 'Search ranking by name'
                  : 'Søg i ranglisten efter navn'
              "
            />
            <button
              v-if="searchQuery"
              type="button"
              class="ranking-search-clear"
              :aria-label="$englishPreferred ? 'Clear search' : 'Ryd søgning'"
              @click="searchQuery = ''"
            >
              <i class="material-icons" aria-hidden="true">close</i>
            </button>
          </label>
          <span class="ranking-result-count" aria-live="polite">
            {{ resultSummary }}
          </span>
        </section>

        <section id="rankListDiv" aria-live="polite">
          <div v-if="paginatedRows.length > 0" class="ranking-list">
            <div
              v-for="rankRow in paginatedRows"
              :key="`${rankRow.ranking}-${rankRow.username}`"
              class="ranking-row"
              :class="`is-rank-${rankRow.ranking}`"
            >
              <span class="ranking-position">{{ rankRow.ranking }}.</span>
              <span class="ranking-name">{{ rankRow.username }}</span>
              <span class="ranking-total">{{ rankRow.total }}</span>
            </div>
          </div>

          <div v-else class="ranking-no-results">
            <i class="material-icons" aria-hidden="true">person_search</i>
            <strong>
              {{
                $englishPreferred
                  ? "No names match your search"
                  : "Ingen navne matcher din søgning"
              }}
            </strong>
            <button type="button" @click="searchQuery = ''">
              {{ $englishPreferred ? "Clear search" : "Ryd søgning" }}
            </button>
          </div>

          <nav
            v-if="pageCount > 1"
            class="ranking-pagination"
            :aria-label="$englishPreferred ? 'Ranking pages' : 'Ranglistesider'"
          >
            <button
              type="button"
              class="ranking-page-step"
              :disabled="currentPageNumber === 1"
              @click="goToPage(currentPageNumber - 1)"
            >
              <i class="material-icons" aria-hidden="true">chevron_left</i>
              <span>{{ $englishPreferred ? "Previous" : "Forrige" }}</span>
            </button>

            <div class="ranking-page-numbers">
              <template v-for="item in paginationItems" :key="item.key">
                <span
                  v-if="item.type === 'ellipsis'"
                  class="ranking-page-ellipsis"
                >
                  …
                </span>
                <button
                  v-else
                  type="button"
                  class="ranking-page-number"
                  :class="{ 'is-active': item.page === currentPageNumber }"
                  :aria-current="
                    item.page === currentPageNumber ? 'page' : undefined
                  "
                  :aria-label="
                    $englishPreferred
                      ? `Go to page ${item.page}`
                      : `Gå til side ${item.page}`
                  "
                  @click="goToPage(item.page)"
                >
                  {{ item.page }}
                </button>
              </template>
            </div>

            <button
              type="button"
              class="ranking-page-step"
              :disabled="currentPageNumber === pageCount"
              @click="goToPage(currentPageNumber + 1)"
            >
              <span>{{ $englishPreferred ? "Next" : "Næste" }}</span>
              <i class="material-icons" aria-hidden="true">chevron_right</i>
            </button>
          </nav>

          <p v-if="pageCount > 1" class="ranking-page-status">
            {{
              $englishPreferred
                ? `Page ${currentPageNumber} of ${pageCount}`
                : `Side ${currentPageNumber} af ${pageCount}`
            }}
          </p>
        </section>
      </template>

      <div v-else-if="loading" class="ranking-status-card">
        <i class="material-icons ranking-status-spinner" aria-hidden="true"
          >sync</i
        >
        {{ $englishPreferred ? "Fetching ranking list" : "Henter rangliste…" }}
      </div>
      <div v-else-if="!online" class="ranking-status-card is-error">
        <i class="material-icons" aria-hidden="true">cloud_off</i>
        {{
          $englishPreferred
            ? "Could not fetch the ranking. You may be offline."
            : "Kunne ikke hente ranglisten. Du er muligvis offline."
        }}
      </div>
      <div v-else class="ranking-status-card">
        <i class="material-icons" aria-hidden="true">leaderboard</i>
        {{ $englishPreferred ? "The ranking is empty" : "Ranglisten er tom" }}
      </div>
    </div>
  </main>
</template>

<script>
import NavHeader from "@/components/navbars/NavHeader.vue";
import axios from "axios";

export default {
  name: "RankingPage",
  components: {
    NavHeader,
  },
  props: {
    endpoint: {
      type: String,
      required: true,
    },
    headingDanish: {
      type: String,
      required: true,
    },
    headingEnglish: {
      type: String,
      required: true,
    },
    isFinn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rankRows: [],
      online: true,
      loading: true,
      pageSize: 10,
      currentPageNumber: 1,
      searchQuery: "",
    };
  },
  computed: {
    filteredRankRows() {
      const query = this.searchQuery.toLocaleLowerCase("da-DK");
      if (!query) {
        return this.rankRows;
      }
      return this.rankRows.filter((row) =>
        String(row.username || "")
          .toLocaleLowerCase("da-DK")
          .includes(query)
      );
    },
    pageCount() {
      return Math.max(
        1,
        Math.ceil(this.filteredRankRows.length / this.pageSize)
      );
    },
    paginatedRows() {
      const start = (this.currentPageNumber - 1) * this.pageSize;
      return this.filteredRankRows.slice(start, start + this.pageSize);
    },
    paginationItems() {
      const pages = new Set([1, this.pageCount]);
      for (
        let page = this.currentPageNumber - 1;
        page <= this.currentPageNumber + 1;
        page++
      ) {
        if (page > 1 && page < this.pageCount) {
          pages.add(page);
        }
      }

      const sortedPages = [...pages].sort((a, b) => a - b);
      const items = [];
      sortedPages.forEach((page, index) => {
        const previousPage = sortedPages[index - 1];
        if (previousPage && page - previousPage > 1) {
          items.push({ type: "ellipsis", key: `ellipsis-${page}` });
        }
        items.push({ type: "page", page, key: `page-${page}` });
      });
      return items;
    },
    resultSummary() {
      if (this.searchQuery) {
        const count = this.filteredRankRows.length;
        return this.$englishPreferred
          ? `${count} ${count === 1 ? "match" : "matches"}`
          : `${count} ${count === 1 ? "resultat" : "resultater"}`;
      }
      return this.$englishPreferred
        ? `${this.rankRows.length} participants`
        : `${this.rankRows.length} deltagere`;
    },
  },
  watch: {
    searchQuery() {
      this.currentPageNumber = 1;
    },
    pageCount(newPageCount) {
      if (this.currentPageNumber > newPageCount) {
        this.currentPageNumber = newPageCount;
      }
    },
  },
  methods: {
    goToPage(pageNumber) {
      this.currentPageNumber = Math.min(
        Math.max(pageNumber, 1),
        this.pageCount
      );
    },
    async prepareData() {
      this.loading = true;
      this.online = true;
      try {
        const response = await axios.get(this.endpoint);
        this.rankRows = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        this.online = false;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.prepareData();
  },
};
</script>
